import { t } from "i18next";
import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { restockNotification } from "@repo/shopifyV2/api/restockNotification";
import type { IProductDetailNode } from "@repo/shopifyV2/interface/product";
import { validateEmail } from "@repo/shopifyV2/utils/regex";

import Button from "@atoms/Button";
import Input from "@atoms/Input";
import Select from "@atoms/Select";

import { NOTIFICATIONS } from "@const/notifications";

import useLanguage from "@hooks/useLanguage";

import { useLayoutStore } from "@store/layout";

interface IFormData {
  email: string;
}

interface IProps {
  variantList: { node: IProductDetailNode }[];
  defaultVariantId: string;
}

export default function RestockNotification({ variantList, defaultVariantId }: IProps) {
  const { openToast, openModal } = useLayoutStore();
  const { lang } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [variantId, setVariantId] = useState(defaultVariantId);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormData>();

  const displayError = (key: keyof IFormData) => {
    return { isError: true, errorMessage: errors[key]?.message };
  };

  // 재입고 알림을 받을 수 있는 대상은 품절된 variant 뿐이다. 라벨 형식은 ProductInformation의 사이즈 select와 동일하게 맞춘다
  const outOfStockOptions = useMemo(
    () =>
      variantList
        .filter(({ node }) => !node.availableForSale)
        .map(({ node }) => {
          const size = node.selectedOptions.find((option) => option.name === "Size")?.value ?? "";

          return { name: `${size} - ${lang === "jp" ? "再入荷通知" : "Out of Stock"}`, value: node.id };
        }),
    [variantList, lang],
  );

  const onSubmit = handleSubmit(async (formData) => {
    setIsLoading(true);

    try {
      const response = await restockNotification({ variantId, domain: lang, value: formData.email });
      if (response) {
        openToast(NOTIFICATIONS.SUCCESS_RESTOCK);
        openModal(null);
      } else {
        openToast(NOTIFICATIONS.WARNING_RESTOCK);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`[LAMBDA][fetchRestock] Failed to Fetch : ${message}`);
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <form className="pc:gap-5 flex flex-col gap-4" noValidate onSubmit={onSubmit}>
      <Select
        as="square"
        onChange={(e) => {
          setVariantId(e.target.value);
        }}
        options={outOfStockOptions}
        value={variantId}
      />
      <div className="flex flex-col gap-1">
        <span className="font-fuzzy-bold">{t("product.restock_notification.description")}</span>
        <div className="w-full">
          <Input
            {...register("email", {
              required: { value: true, message: t("error.necessary_email") },
              validate(value) {
                if (!validateEmail(value)) return t("login.invalid_email");
              },
            })}
            {...(errors.email && displayError("email"))}
            placeholder="Email"
            type="email"
          />
        </div>
      </div>
      <Button as="square" className="w-full" loading={isLoading} type="submit">
        メールで通知を受け取る
      </Button>
    </form>
  );
}
